// V4-FRONT-ANY-USAGE batch 4 2026-05-28
/**
 * Contenu réutilisable de la vue "Charte graphique" — sans header.
 *
 * Chantier P (2026-05) — extrait depuis l'ancien `BrandConfigTab.tsx`
 * (qui contenait le header X + le contenu). Ce composant ne rend que le
 * **contenu** (URL + re-extract + palette + fonts + images + direction artistique)
 * sans son propre titre/close. Il est consommé par :
 *
 *  - `PositioningTab` quand `mode === 'brandConfig'` (point d'entrée unique).
 *  - éventuellement par d'autres surfaces (Settings, modale dédiée).
 *
 * Le header de PositioningTab fournit déjà titre + bouton close, donc ce
 * composant n'a pas besoin de les rendre.
 */
import { useEffect, useRef, useState } from 'react';
import { Input } from '../../ui/input';
import { RotateCcw, Loader2, AlertTriangle, Copy, Check, Plus, Trash2, Edit3, X, Upload } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { brandConfigService } from '../../../services/brandConfigService';
import { mediaService } from '../../../services/mediaService';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { useReadOnly } from '../../../hooks/useReadOnly';
import type { BrandConfig, BrandColorRole, BrandImageType } from '../../../types/brandConfig';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { copyToClipboard } from '../../../utils/clipboard';

// V4-BRAND-02 2026-05-29 — backoff exponentiel : 1s → 2s → 4s → 8s → 16s → 30s max.
// Reset à 1s quand on reçoit une réponse valide. Circuit-breaker après 5 échecs
// 5xx consécutifs → on stoppe le polling et on affiche un banner "Connexion perdue".
const POLL_BACKOFF_SCHEDULE_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const POLL_MAX_FAIL_5XX = 5;
const POLL_MAX_DURATION_MS = 5 * 60 * 1000;
// V4-BRAND-01 2026-05-29 — au-delà de cette durée pending, on affiche un warning
// "extraction prend plus de temps que prévu" (toujours en pending mais visible).
const POLL_SLOW_THRESHOLD_MS = 30 * 1000;

/**
 * Fallback heuristique pour extraire 3-4 keywords depuis l'`artistic_direction`
 * quand le backend n'a pas (encore) fourni `direction_keywords` (UX backlog 4.2).
 *
 * Stratégie simple : on prend les expressions entre guillemets « » ou " ",
 * les adjectifs distinctifs courants, et on plafonne à 5 items.
 */
function extractKeywordsFallback(text: string): string[] {
  if (!text) return [];
  const keywords: string[] = [];
  // 1. Expressions entre guillemets (français « ... » ou anglais "...")
  const quoteMatches = [...text.matchAll(/«\s*([^»]+?)\s*»|"\s*([^"]+?)\s*"/g)];
  for (const m of quoteMatches) {
    const captured = (m[1] || m[2] || '').trim();
    if (captured && captured.length <= 30) keywords.push(captured);
  }
  // 2. Adjectifs/styles fréquents dans les directions artistiques.
  const stylePatterns = [
    'minimaliste', 'moderne', 'épuré', 'tech', 'corporate', 'élégant',
    'audacieux', 'organique', 'futuriste', 'rétro', 'vintage', 'premium',
    'naturel', 'sombre', 'lumineux', 'pastel', 'vibrant',
  ];
  for (const w of stylePatterns) {
    if (text.toLowerCase().includes(w) && !keywords.some(k => k.toLowerCase().includes(w))) {
      keywords.push(w.charAt(0).toUpperCase() + w.slice(1));
    }
  }
  return Array.from(new Set(keywords)).slice(0, 5);
}

/**
 * Calcule la couleur de texte la plus contrastée (noir/blanc) pour un fond hex.
 * UX backlog 4.3 : utilisé dans le preview de charte pour lisibilité auto.
 */
function getContrastTextColor(hex: string): string {
  if (!hex || !hex.startsWith('#')) return '#000000';
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  // Luminance perçue (W3C YIQ formula)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#0A0A0A' : '#FFFFFF';
}

export function BrandConfigContent() {
  const { t } = useLanguage();
  const { currentSpace } = useAuthContext();
  const readOnly = useReadOnly();
  const bc = t.brandConfig;

  const [config, setConfig] = useState<BrandConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [landingUrl, setLandingUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [copiedHex, setCopiedHex] = useState<string | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);

  // UX fix 20-05 — Mode saisie manuelle pour les utilisateurs sans site web.
  // Toggle qui ouvre des formulaires inline pour ajouter couleurs/fonts/images.
  const [manualMode, setManualMode] = useState(false);
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [newColor, setNewColor] = useState<{ hex: string; role: BrandColorRole }>({ hex: '#5500FF', role: 'primary' });
  const [newFont, setNewFont] = useState<string>('');
  const [newImage, setNewImage] = useState<{ url: string; type: BrandImageType }>({ url: '', type: 'logo' });
  // V4-BRAND-FONT-IMAGE-EDIT — édition fonts + remplacement images (indépendant de manualMode).
  const [editingFontIdx, setEditingFontIdx] = useState<number | null>(null);
  const [editFontFamily, setEditFontFamily] = useState('');
  const [editFontWeights, setEditFontWeights] = useState('');

  // V4-BRAND-EDIT-DIRECTION 2026-06-04 — édition manuelle a posteriori de la direction
  // artistique + des tags (direction_keywords), indépendante du manualMode. Persiste via
  // le même PUT whole-config (persistManualConfig).
  const [isEditingDirection, setIsEditingDirection] = useState(false);
  const [editedDirection, setEditedDirection] = useState('');
  const [editedKeywords, setEditedKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');

  // Fetch initial + sync sur changement de space
  useEffect(() => {
    if (!currentSpace?.id) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    brandConfigService
      .get(currentSpace.id)
      .then((data) => {
        if (cancelled) return;
        setConfig(data);
        setLandingUrl(data?.landing_page_url ?? '');
      })
      .catch((e) => {
        Logger.error('[BrandConfigContent] Failed to fetch config', e);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSpace?.id]);

  // Polling pendant extraction pending — UX backlog 4.4 + V4-BRAND-02 2026-05-29.
  // Backoff exponentiel via setTimeout récursif (1→2→4→8→16→30s) + circuit-breaker
  // après 5 échecs 5xx consécutifs. Affiche un banner "Connexion perdue" si breaker
  // déclenché.
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalStatusReachedRef = useRef<boolean>(false);
  const [pollingLost, setPollingLost] = useState(false);
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    finalStatusReachedRef.current = false; // reset au changement de space
    setPollingLost(false);
    setIsSlow(false);
  }, [currentSpace?.id]);

  useEffect(() => {
    if (!currentSpace?.id) return;
    if (config?.extraction_status !== 'pending') {
      pollStartedAtRef.current = null;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setIsSlow(false);
      // Marque l'arrivée d'un status final (succeeded/failed/null/undefined) pour
      // empêcher un re-arm si re-render avec un état intermédiaire.
      if (config?.extraction_status === 'succeeded' || config?.extraction_status === 'failed') {
        finalStatusReachedRef.current = true;
        Logger.info(`[BrandConfigContent] Extraction final status: ${config.extraction_status}`);
      }
      return;
    }

    // Si on a déjà atteint un status final dans cette session, ne pas re-poll.
    if (finalStatusReachedRef.current) return;

    if (pollStartedAtRef.current === null) {
      pollStartedAtRef.current = Date.now();
    }

    let cancelled = false;
    let attempt = 0;             // index dans POLL_BACKOFF_SCHEDULE_MS
    let consecutive5xx = 0;      // pour circuit-breaker

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      pollTimeoutRef.current = setTimeout(tick, delayMs);
    };

    // V4-SETTINGS-LOCKEDSECTION-TS-LINT — `tick` en déclaration de fonction (hoistée) pour la
    // récursion mutuelle avec `scheduleNext` sans no-use-before-define (config functions:false).
    async function tick() {
      if (cancelled || !currentSpace?.id) return;
      try {
        const data = await brandConfigService.get(currentSpace.id, { forceFresh: true });
        if (cancelled) return;
        consecutive5xx = 0; // reset compteur sur succès réseau
        setPollingLost(false);
        attempt = 0; // reset backoff
        const newStatus = data?.extraction_status;
        setConfig(data);
        if (newStatus !== 'pending') {
          Logger.info(`[BrandConfigContent] Poll detected final status: ${newStatus}`);
          pollStartedAtRef.current = null;
          setIsSlow(false);
          return; // pas de re-schedule
        }
        // V4-BRAND-01 — warning "extraction lente" si > 30s de pending
        const elapsed = Date.now() - (pollStartedAtRef.current ?? Date.now());
        setIsSlow(elapsed > POLL_SLOW_THRESHOLD_MS);
        if (elapsed > POLL_MAX_DURATION_MS) {
          Logger.warn('[BrandConfigContent] Poll timeout — stopping');
          pollStartedAtRef.current = null;
          return;
        }
        // status toujours pending → re-schedule avec backoff exponentiel
        const nextDelay = POLL_BACKOFF_SCHEDULE_MS[Math.min(attempt, POLL_BACKOFF_SCHEDULE_MS.length - 1)];
        attempt += 1;
        scheduleNext(nextDelay);
      } catch (e: unknown) {
        // V4-FRONT-ANY-USAGE batch 5 2026-05-29 — polling : on garde l'accès status
        // pour la logique circuit-breaker 5xx (pas de toast ici, juste log + retry).
        if (cancelled) return;
        const status = (e as { response?: { status?: number } } | null | undefined)?.response?.status;
        if (status && status >= 500) {
          consecutive5xx += 1;
          Logger.warn(`[BrandConfigContent] Polling 5xx (${consecutive5xx}/${POLL_MAX_FAIL_5XX})`, e);
          if (consecutive5xx >= POLL_MAX_FAIL_5XX) {
            Logger.error('[BrandConfigContent] Circuit breaker tripped — stopping polling');
            setPollingLost(true);
            return; // pas de re-schedule → user devra refresh manuel
          }
        } else {
          Logger.error('[BrandConfigContent] Polling failed', e);
        }
        const nextDelay = POLL_BACKOFF_SCHEDULE_MS[Math.min(attempt, POLL_BACKOFF_SCHEDULE_MS.length - 1)];
        attempt += 1;
        scheduleNext(nextDelay);
      }
    }

    // démarrage immédiat
    scheduleNext(POLL_BACKOFF_SCHEDULE_MS[0]);

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [config?.extraction_status, currentSpace?.id]);

  /** UX backlog 4.4 : refresh manuel forcé (utile si polling stuck). */
  const handleForceRefresh = async () => {
    if (!currentSpace?.id) return;
    try {
      // forceFresh: bypass cache pour vraiment forcer un re-fetch backend (fix 3.2).
      const data = await brandConfigService.get(currentSpace.id, { forceFresh: true });
      setConfig(data);
      Logger.info(`[BrandConfigContent] Manual refresh → status=${data?.extraction_status}`);
    } catch (e) {
      Logger.error('[BrandConfigContent] Manual refresh failed', e);
      toastService.error(bc.refreshError ?? 'Refresh failed');
    }
  };

  const handleExtract = async () => {
    if (!currentSpace?.id || !landingUrl.trim()) return;
    // V4-FRONT-BRAND-EXTRACT-POLL-NO-RESTART — re-arme le polling après une extraction
    // déjà terminée (succeeded/failed) dans cette même session, sinon l'effect return immédiatement.
    finalStatusReachedRef.current = false;
    setIsExtracting(true);
    try {
      await brandConfigService.extract(currentSpace.id, landingUrl.trim());
      const data = await brandConfigService.get(currentSpace.id, { forceFresh: true });
      setConfig(data);
    } catch (e: unknown) {
      // V4-FRONT-ANY-USAGE batch 5 2026-05-29
      Logger.error('[BrandConfigContent] Extract failed', e);
      toastService.error(extractApiErrorMessage(e, bc.saveError || 'Erreur lors de l\'extraction'));
    } finally {
      setIsExtracting(false);
    }
  };

  // UX fix 20-05 — Builder pour le config manuel.
  // Si pas de config existant (1er ajout), on crée un "shell" avec status succeeded.
  const buildEmptyConfig = (): BrandConfig => ({
    landing_page_url: landingUrl || '',
    extracted_at: new Date().toISOString(),
    extraction_status: 'succeeded',
    colors: [],
    fonts: [],
    images: [],
    artistic_direction: '',
  });

  const persistManualConfig = async (updated: BrandConfig): Promise<void> => {
    if (!currentSpace?.id) return;
    setIsSavingManual(true);
    try {
      const saved = await brandConfigService.update(currentSpace.id, updated);
      setConfig(saved);
    } catch (e: unknown) {
      // V4-FRONT-ANY-USAGE batch 5 2026-05-29
      Logger.error('[BrandConfigContent] Manual update failed', e);
      toastService.error(extractApiErrorMessage(e, bc.saveError ?? 'Erreur lors de la sauvegarde'));
    } finally {
      setIsSavingManual(false);
    }
  };

  const handleAddColor = async () => {
    const hex = newColor.hex.trim();
    if (!hex.match(/^#[0-9a-fA-F]{6}$/)) {
      toastService.error(bc.invalidHex ?? 'Format hex invalide (ex: #5500FF)');
      return;
    }
    const base = config ?? buildEmptyConfig();
    const updated: BrandConfig = {
      ...base,
      colors: [...base.colors, { hex: hex.toUpperCase(), role: newColor.role, weight: 1 }],
    };
    await persistManualConfig(updated);
    setNewColor({ hex: '#5500FF', role: 'primary' });
  };

  const handleRemoveColor = async (idx: number) => {
    if (!config) return;
    const updated: BrandConfig = { ...config, colors: config.colors.filter((_, i) => i !== idx) };
    await persistManualConfig(updated);
  };

  const handleAddFont = async () => {
    const family = newFont.trim();
    if (!family) return;
    const base = config ?? buildEmptyConfig();
    // Heuristique : si la famille contient des espaces ou est dans un set connu, on suppose Google Font.
    const isGoogleFont = /^[A-Za-z][A-Za-z0-9\s]+$/.test(family);
    const updated: BrandConfig = {
      ...base,
      fonts: [...base.fonts, { family, weights: [400, 700], is_google_font: isGoogleFont }],
    };
    await persistManualConfig(updated);
    setNewFont('');
  };

  const handleRemoveFont = async (idx: number) => {
    if (!config) return;
    const updated: BrandConfig = { ...config, fonts: config.fonts.filter((_, i) => i !== idx) };
    await persistManualConfig(updated);
  };

  const handleAddImage = async () => {
    const url = newImage.url.trim();
    if (!url.match(/^https?:\/\//)) {
      toastService.error(bc.invalidImageUrl ?? 'URL invalide (doit commencer par http(s)://)');
      return;
    }
    const base = config ?? buildEmptyConfig();
    const updated: BrandConfig = {
      ...base,
      images: [...base.images, { url, type: newImage.type }],
    };
    await persistManualConfig(updated);
    setNewImage({ url: '', type: 'logo' });
  };

  const handleRemoveImage = async (idx: number) => {
    if (!config) return;
    const updated: BrandConfig = { ...config, images: config.images.filter((_, i) => i !== idx) };
    await persistManualConfig(updated);
  };

  // V4-BRAND-FONT-IMAGE-EDIT — édite une font (famille + poids) à l'index donné.
  const startEditFont = (idx: number) => {
    const f = config?.fonts[idx];
    if (!f) return;
    setEditFontFamily(f.family);
    setEditFontWeights((f.weights ?? []).join(', '));
    setEditingFontIdx(idx);
  };

  const handleSaveFont = async () => {
    if (!config || editingFontIdx === null) return;
    const family = editFontFamily.trim();
    if (!family) return;
    const weights = editFontWeights
      .split(',')
      .map((w) => parseInt(w.trim(), 10))
      .filter((w) => Number.isFinite(w));
    const updated: BrandConfig = {
      ...config,
      fonts: config.fonts.map((f, i) => (i === editingFontIdx ? { ...f, family, weights } : f)),
    };
    setEditingFontIdx(null);
    await persistManualConfig(updated);
  };

  // V4-BRAND-FONT-IMAGE-EDIT — remplace l'image à l'index via upload MinIO (mediaService).
  const handleReplaceImage = async (idx: number, file: File) => {
    if (!config || !currentSpace?.id) return;
    setIsSavingManual(true);
    try {
      const uploaded = await mediaService.uploadFile(file);
      const updated: BrandConfig = {
        ...config,
        images: config.images.map((im, i) => (i === idx ? { ...im, url: uploaded.url } : im)),
      };
      const saved = await brandConfigService.update(currentSpace.id, updated);
      setConfig(saved);
    } catch (e: unknown) {
      Logger.error('[BrandConfigContent] Replace image failed', e);
      toastService.error(extractApiErrorMessage(e, bc.saveError ?? 'Erreur lors de la sauvegarde'));
    } finally {
      setIsSavingManual(false);
    }
  };

  // V4-BRAND-EDIT-DIRECTION — entrée en édition AD/tags : pré-remplit le textarea avec
  // l'AD courante et les keywords (backend si présents, sinon fallback heuristique).
  const startEditDirection = () => {
    const base = config;
    setEditedDirection(base?.artistic_direction ?? '');
    const kws = base?.direction_keywords && base.direction_keywords.length > 0
      ? base.direction_keywords
      : (base?.artistic_direction ? extractKeywordsFallback(base.artistic_direction) : []);
    setEditedKeywords(kws);
    setNewKeyword('');
    setIsEditingDirection(true);
  };

  const handleAddKeyword = () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    if (editedKeywords.some((k) => k.toLowerCase() === kw.toLowerCase())) {
      setNewKeyword('');
      return;
    }
    setEditedKeywords([...editedKeywords, kw]);
    setNewKeyword('');
  };

  const handleRemoveKeyword = (kw: string) => {
    setEditedKeywords(editedKeywords.filter((k) => k !== kw));
  };

  const handleSaveDirection = async () => {
    const base = config ?? buildEmptyConfig();
    const updated: BrandConfig = {
      ...base,
      artistic_direction: editedDirection.trim(),
      direction_keywords: editedKeywords,
    };
    await persistManualConfig(updated);
    setIsEditingDirection(false);
  };

  const handleCopyHex = async (hex: string) => {
    try {
      await copyToClipboard(hex);
      setCopiedHex(hex);
      // UX backlog 4.1 : 2.5s au lieu de 1.5s pour laisser le temps de voir le feedback Check.
      setTimeout(() => setCopiedHex(null), 2500);
    } catch {
      toastService.error('Copy failed');
    }
  };

  const getRoleLabel = (role: string): string => {
    const map: Record<string, string> = {
      primary: bc.colorRolePrimary ?? 'Primary',
      secondary: bc.colorRoleSecondary ?? 'Secondary',
      accent: bc.colorRoleAccent ?? 'Accent',
      background: bc.colorRoleBackground ?? 'Background',
      text: bc.colorRoleText ?? 'Text',
      muted: bc.colorRoleMuted ?? 'Muted',
    };
    return map[role] ?? role;
  };

  const getImageTypeLabel = (type: string): string => {
    const map: Record<string, string> = {
      logo: bc.imageTypeLogo ?? 'Logo',
      hero: bc.imageTypeHero ?? 'Hero',
      additional: bc.imageTypeAdditional ?? 'Additional',
    };
    return map[type] ?? type;
  };

  const isPending = config?.extraction_status === 'pending';
  const isFailed = config?.extraction_status === 'failed';

  // V4-BRAND-01 2026-05-29 — Action "Réessayer" : reset puis re-déclenche
  // l'extraction sur la dernière URL connue.
  const handleRetryExtraction = async () => {
    if (!currentSpace?.id) return;
    // V4-FRONT-BRAND-EXTRACT-POLL-NO-RESTART — re-arme le polling après un échec déjà observé.
    finalStatusReachedRef.current = false;
    try {
      await brandConfigService.reset(currentSpace.id);
      toastService.success(bc.extractionReset ?? 'Extraction reset');
      // Re-lance l'extraction si on a une URL en mémoire
      const urlToUse = (landingUrl || config?.landing_page_url || '').trim();
      if (urlToUse) {
        setIsExtracting(true);
        await brandConfigService.extract(currentSpace.id, urlToUse);
        const fresh = await brandConfigService.get(currentSpace.id, { forceFresh: true });
        setConfig(fresh);
      } else {
        // Pas d'URL → on rafraîchit juste la config (status null après reset)
        const fresh = await brandConfigService.get(currentSpace.id, { forceFresh: true });
        setConfig(fresh);
      }
    } catch (e: unknown) {
      // V4-FRONT-ANY-USAGE batch 5 2026-05-29
      Logger.error('[BrandConfigContent] Reset/Retry failed', e);
      toastService.error(extractApiErrorMessage(e, bc.extractionResetError ?? 'Could not reset extraction'));
    } finally {
      setIsExtracting(false);
    }
  };

  // V4-BRAND-01 2026-05-29 — Action "Configurer manuellement" : reset + ouvre
  // le mode manuel pour saisir couleurs/fonts/images à la main.
  const handleSwitchToManual = async () => {
    if (!currentSpace?.id) return;
    try {
      await brandConfigService.reset(currentSpace.id);
      const fresh = await brandConfigService.get(currentSpace.id, { forceFresh: true });
      setConfig(fresh);
    } catch (e) {
      Logger.warn('[BrandConfigContent] Manual switch reset failed (continuing)', e);
    } finally {
      setManualMode(true);
    }
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
        </div>
      ) : (
        <>
          {/* Description du module */}
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {bc.description}
          </p>

          {/* Landing page URL + bouton extract */}
          <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <label className="text-xs font-medium block" style={{ color: 'var(--color-text-secondary)' }}>
              {bc.landingUrl ?? 'Landing page URL'}
            </label>
            <div className="flex gap-2">
              <Input
                type="url"
                value={landingUrl}
                onChange={(e) => setLandingUrl(e.target.value)}
                placeholder={bc.landingUrlPlaceholder ?? 'https://...'}
                className="flex-1"
                disabled={readOnly || isPending || isExtracting}
              />
              {!readOnly && (
                <AnimatedIconButton
                  icon={isExtracting || isPending ? Loader2 : RotateCcw}
                  label={isExtracting || isPending ? (bc.reExtracting ?? 'Extracting…') : (bc.reExtract ?? 'Re-extract')}
                  onClick={handleExtract}
                  disabled={!landingUrl.trim() || isExtracting || isPending}
                  variant="default"
                  iconClassName={isExtracting || isPending ? 'animate-spin' : ''}
                />
              )}
            </div>
          </div>

          {/* UX fix 20-05 — Toggle saisie manuelle pour utilisateurs sans site web.
              Permet d'ajouter couleurs, fonts et images directement sans extraction. */}
          {!readOnly && (
            <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <button
                type="button"
                onClick={() => setManualMode((v) => !v)}
                className="flex items-center gap-2 text-xs font-medium hover:opacity-70 transition-opacity"
                style={{ color: 'var(--color-primary)' }}
                disabled={isSavingManual}
              >
                <Edit3 className="w-3.5 h-3.5" />
                {manualMode
                  ? (bc.manualModeHide ?? 'Masquer la saisie manuelle')
                  : (bc.manualModeShow ?? 'Pas de site web ? Saisir manuellement')}
              </button>

              {manualMode && (
                <div className="space-y-4 pt-2" style={{ borderTop: '1px dashed var(--color-border)' }}>
                  {/* Ajouter une couleur */}
                  <div>
                    <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {bc.manualAddColor ?? 'Ajouter une couleur'}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={newColor.hex}
                        onChange={(e) => setNewColor({ ...newColor, hex: e.target.value.toUpperCase() })}
                        className="w-10 h-10 rounded-md cursor-pointer border"
                        style={{ borderColor: 'var(--color-border)' }}
                        disabled={isSavingManual}
                      />
                      <Input
                        type="text"
                        value={newColor.hex}
                        onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })}
                        placeholder="#5500FF"
                        className="flex-1 font-mono text-xs"
                        disabled={isSavingManual}
                      />
                      <select
                        value={newColor.role}
                        onChange={(e) => setNewColor({ ...newColor, role: e.target.value as BrandColorRole })}
                        className="px-2 py-2 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        disabled={isSavingManual}
                      >
                        <option value="primary">{bc.colorRolePrimary ?? 'Primary'}</option>
                        <option value="secondary">{bc.colorRoleSecondary ?? 'Secondary'}</option>
                        <option value="accent">{bc.colorRoleAccent ?? 'Accent'}</option>
                        <option value="background">{bc.colorRoleBackground ?? 'Background'}</option>
                        <option value="text">{bc.colorRoleText ?? 'Text'}</option>
                        <option value="muted">{bc.colorRoleMuted ?? 'Muted'}</option>
                      </select>
                      <AnimatedIconButton
                        icon={isSavingManual ? Loader2 : Plus}
                        label={bc.add ?? 'Ajouter'}
                        onClick={handleAddColor}
                        disabled={isSavingManual}
                        variant="default"
                        iconClassName={isSavingManual ? 'animate-spin' : ''}
                      />
                    </div>
                  </div>

                  {/* Ajouter une font */}
                  <div>
                    <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {bc.manualAddFont ?? 'Ajouter une police (nom Google Font ou nom de famille)'}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={newFont}
                        onChange={(e) => setNewFont(e.target.value)}
                        placeholder={bc.manualFontPlaceholder ?? 'Ex: Inter, Roboto, Poppins…'}
                        className="flex-1 text-xs"
                        disabled={isSavingManual}
                      />
                      <AnimatedIconButton
                        icon={isSavingManual ? Loader2 : Plus}
                        label={bc.add ?? 'Ajouter'}
                        onClick={handleAddFont}
                        disabled={isSavingManual || !newFont.trim()}
                        variant="default"
                        iconClassName={isSavingManual ? 'animate-spin' : ''}
                      />
                    </div>
                  </div>

                  {/* Ajouter une image */}
                  <div>
                    <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {bc.manualAddImage ?? 'Ajouter une image (URL publique)'}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        value={newImage.url}
                        onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
                        placeholder="https://exemple.com/logo.png"
                        className="flex-1 text-xs"
                        disabled={isSavingManual}
                      />
                      <select
                        value={newImage.type}
                        onChange={(e) => setNewImage({ ...newImage, type: e.target.value as BrandImageType })}
                        className="px-2 py-2 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        disabled={isSavingManual}
                      >
                        <option value="logo">{bc.imageTypeLogo ?? 'Logo'}</option>
                        <option value="hero">{bc.imageTypeHero ?? 'Hero'}</option>
                        <option value="additional">{bc.imageTypeAdditional ?? 'Additional'}</option>
                      </select>
                      <AnimatedIconButton
                        icon={isSavingManual ? Loader2 : Plus}
                        label={bc.add ?? 'Ajouter'}
                        onClick={handleAddImage}
                        disabled={isSavingManual || !newImage.url.trim()}
                        variant="default"
                        iconClassName={isSavingManual ? 'animate-spin' : ''}
                      />
                    </div>
                  </div>

                  <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
                    {bc.manualHint ?? 'Les éléments ajoutés ici sont sauvegardés immédiatement et utilisés par Raiso.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Status banner — pending (avec warning lent si > 30s) */}
          {isPending && (
            <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(99, 102, 241, 0.08)', color: 'var(--color-primary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{bc.statusPending ?? 'Extraction en cours…'}</span>
            </div>
          )}
          {/* V4-BRAND-01 — warning "lent" si pending > 30s */}
          {isPending && isSlow && (
            <div className="flex items-start gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)', color: 'var(--color-warning, #B45309)' }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{bc.extractionSlow ?? 'Extraction is taking longer than expected.'}</span>
            </div>
          )}
          {/* V4-BRAND-02 — circuit-breaker banner : polling stoppé après 5 échecs 5xx */}
          {pollingLost && (
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', color: 'var(--color-danger)' }}>
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {bc.pollingLost ?? 'Connection lost, reconnecting…'}
              </span>
              <button
                type="button"
                onClick={handleForceRefresh}
                className="text-xs underline hover:opacity-80"
              >
                {t.common?.refresh ?? 'Refresh'}
              </button>
            </div>
          )}
          {/* V4-BRAND-01 — Panneau failed enrichi : retry + manuel.
              Affiché systématiquement si status === 'failed' (même sans
              `config.error` côté backend — soft override sera traité comme failed). */}
          {isFailed && (
            <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-danger)' }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm" style={{ color: 'var(--color-danger)' }}>
                    {bc.statusFailed ?? 'Extraction failed'}
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {bc.extractionFailed ?? 'We could not analyze your site. You can retry or configure manually.'}
                  </p>
                  {config?.error && (
                    <p className="text-[11px] mt-1 opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
                      {config.error}
                    </p>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-2 ml-7">
                  <AnimatedIconButton
                    icon={isExtracting ? Loader2 : RotateCcw}
                    label={bc.extractionRetry ?? 'Retry'}
                    onClick={handleRetryExtraction}
                    disabled={isExtracting}
                    variant="default"
                    iconClassName={isExtracting ? 'animate-spin' : ''}
                    testId="brand-config-retry"
                  />
                  <AnimatedIconButton
                    icon={Edit3}
                    label={bc.extractionManual ?? 'Configure manually'}
                    onClick={handleSwitchToManual}
                    variant="neutral"
                    testId="brand-config-manual-switch"
                  />
                </div>
              )}
            </div>
          )}

          {/* Pas de config encore */}
          {!config && !isPending && (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
              {bc.noLandingUrl}
            </p>
          )}

          {/* Direction artistique — UX backlog 4.2 : chips keywords sous le paragraphe.
              V4-BRAND-EDIT-DIRECTION 2026-06-04 : édition manuelle inline (textarea + tags).
              Le bloc s'affiche dès qu'il y a une AD OU que l'utilisateur peut éditer
              (pour permettre d'en saisir une quand elle est vide). */}
          {config && (config.artistic_direction || !readOnly) && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                  {bc.artisticDirection ?? 'Artistic direction'}
                </div>
                {!readOnly && !isEditingDirection && (
                  <button
                    type="button"
                    onClick={startEditDirection}
                    title={t.common.edit ?? 'Éditer'}
                    aria-label={t.common.edit ?? 'Éditer'}
                    disabled={isSavingManual}
                    className="p-1 rounded hover:opacity-70 transition-opacity"
                  >
                    <Edit3 className="w-3.5 h-3.5" style={{ color: 'var(--color-primary)' }} />
                  </button>
                )}
              </div>

              {isEditingDirection ? (
                <div className="space-y-3">
                  <textarea
                    value={editedDirection}
                    onChange={(e) => setEditedDirection(e.target.value)}
                    rows={3}
                    placeholder={bc.artisticDirectionPlaceholder ?? 'Décrivez la direction artistique de votre marque…'}
                    className="w-full text-sm rounded-md p-2 resize-y"
                    style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    disabled={isSavingManual}
                  />
                  {/* Éditeur de tags (direction_keywords) */}
                  <div className="flex flex-wrap gap-1.5">
                    {editedKeywords.map((kw) => (
                      <span
                        key={kw}
                        className="text-[11px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(85, 0, 255, 0.1)', color: 'var(--color-primary)' }}
                      >
                        {kw}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(kw)}
                          title={bc.remove ?? 'Supprimer'}
                          aria-label={`${bc.remove ?? 'Supprimer'} ${kw}`}
                          disabled={isSavingManual}
                          className="hover:opacity-70"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      placeholder={bc.artisticTagPlaceholder ?? 'Ajouter un tag puis Entrée…'}
                      className="flex-1 text-xs"
                      disabled={isSavingManual}
                    />
                    <AnimatedIconButton
                      icon={Plus}
                      label={bc.add ?? 'Ajouter'}
                      onClick={handleAddKeyword}
                      disabled={isSavingManual || !newKeyword.trim()}
                      variant="neutral"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <AnimatedIconButton
                      icon={X}
                      label={t.common.cancel}
                      onClick={() => setIsEditingDirection(false)}
                      disabled={isSavingManual}
                      variant="secondary"
                    />
                    <AnimatedIconButton
                      icon={isSavingManual ? Loader2 : Check}
                      label={t.common.save ?? 'Enregistrer'}
                      onClick={handleSaveDirection}
                      disabled={isSavingManual}
                      variant="default"
                      iconClassName={isSavingManual ? 'animate-spin' : ''}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {config.artistic_direction ? (
                    <p className="text-sm italic" style={{ color: 'var(--color-text-primary)' }}>
                      "{config.artistic_direction}"
                    </p>
                  ) : (
                    <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                      {bc.artisticDirectionEmpty ?? 'Aucune direction artistique. Cliquez sur éditer pour en ajouter une.'}
                    </p>
                  )}
                  {/* Chips keywords : backend (config.direction_keywords) sinon fallback heuristique. */}
                  {config.artistic_direction && (() => {
                    const backendKeywords = config.direction_keywords;
                    const keywords = backendKeywords && backendKeywords.length > 0
                      ? backendKeywords
                      : extractKeywordsFallback(config.artistic_direction);
                    if (!keywords || keywords.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {keywords.slice(0, 5).map(kw => (
                          <span
                            key={kw}
                            className="text-[11px] px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: 'rgba(85, 0, 255, 0.1)',
                              color: 'var(--color-primary)',
                            }}
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Palette */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              {bc.palette ?? 'Palette'}
            </div>
            {!config?.colors || config.colors.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{bc.paletteEmpty ?? 'No colors detected'}</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {config.colors.map((color, idx) => (
                  <div
                    key={color.hex + color.role + idx}
                    className="flex items-center gap-2 p-2 rounded-lg transition-all hover:shadow-sm relative group"
                    style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
                  >
                    <button
                      type="button"
                      onClick={() => handleCopyHex(color.hex)}
                      title={`${(bc.copyColorPrefix ?? 'Copier')} ${color.hex}`}
                      aria-label={`${(bc.copyColorPrefix ?? 'Copier')} ${color.hex}`}
                      className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                    >
                      <div
                        className="w-8 h-8 rounded-md flex-shrink-0"
                        style={{ backgroundColor: color.hex, border: '1px solid var(--color-border)' }}
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                          {getRoleLabel(color.role)}
                        </div>
                        <div className="text-[10px] font-mono uppercase" style={{ color: 'var(--color-text-secondary)' }}>
                          {color.hex}
                        </div>
                      </div>
                      {copiedHex === color.hex ? (
                        <Check className="w-3 h-3" style={{ color: 'var(--color-success)' }} />
                      ) : (
                        <Copy className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
                      )}
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleRemoveColor(idx)}
                        title={bc.remove ?? 'Supprimer'}
                        aria-label={bc.remove ?? 'Supprimer'}
                        disabled={isSavingManual}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded"
                      >
                        <Trash2 className="w-3 h-3" style={{ color: 'var(--color-danger)' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview "rendu avec votre charte" — UX backlog 4.3 + M.6.
              Mini-carte qui utilise les couleurs/fonts extraites pour donner un
              aperçu concret de l'identité visuelle.
              UX backlog M.6 : remontée juste après Palette (était entre Fonts
              et Images, l'utilisateur scrollait trop pour la voir). */}
          {config?.colors && config.colors.length > 0 && (
            (() => {
              // Picks intelligents : role=primary sinon 1ère, role=accent sinon 2ème
              const primary = config.colors.find(c => c.role === 'primary') ?? config.colors[0];
              const accent = config.colors.find(c => c.role === 'accent') ?? config.colors[1] ?? primary;
              const firstFont = config.fonts && config.fonts.length > 0 ? config.fonts[0] : null;
              const titleFont = firstFont?.family;
              const titleColor = getContrastTextColor(primary.hex);
              const accentTextColor = getContrastTextColor(accent.hex);
              return (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {bc.preview ?? 'Preview'}
                  </div>
                  <div
                    className="rounded-xl p-6 transition-all"
                    style={{
                      backgroundColor: primary.hex,
                      color: titleColor,
                      border: `1px solid ${accent.hex}`,
                      fontFamily: firstFont?.is_google_font && titleFont ? `"${titleFont}", sans-serif` : undefined,
                    }}
                  >
                    <h3 style={{ fontFamily: titleFont ? `"${titleFont}", sans-serif` : undefined, fontWeight: 700, fontSize: '18px', margin: 0 }}>
                      {bc.previewTitle ?? 'Titre exemple'}
                    </h3>
                    <p style={{ fontSize: '13px', marginTop: '8px', opacity: 0.85, lineHeight: 1.5 }}>
                      {bc.previewText ?? 'Aperçu d\'un contenu rendu avec votre charte graphique : couleurs, fonts et accents.'}
                    </p>
                    <button
                      type="button"
                      style={{
                        backgroundColor: accent.hex,
                        color: accentTextColor,
                        padding: '8px 18px',
                        borderRadius: '8px',
                        marginTop: '16px',
                        fontWeight: 600,
                        fontSize: '13px',
                        border: 'none',
                        cursor: 'default',
                      }}
                      tabIndex={-1}
                    >
                      {bc.previewCta ?? 'Bouton CTA'}
                    </button>
                  </div>
                </div>
              );
            })()
          )}

          {/* Fonts */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              {bc.fonts ?? 'Fonts'}
            </div>
            {!config?.fonts || config.fonts.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{bc.fontsEmpty ?? 'No fonts detected'}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {config.fonts.map((font, idx) => (
                  editingFontIdx === idx ? (
                    <div
                      key={font.family + idx}
                      className="px-2 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5"
                      style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
                    >
                      <input
                        value={editFontFamily}
                        onChange={(e) => setEditFontFamily(e.target.value)}
                        placeholder="Inter"
                        className="text-xs px-2 py-1 rounded"
                        style={{ width: 110, backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                      />
                      <input
                        value={editFontWeights}
                        onChange={(e) => setEditFontWeights(e.target.value)}
                        placeholder="400, 700"
                        className="text-xs px-2 py-1 rounded"
                        style={{ width: 78, backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                      />
                      <button type="button" onClick={handleSaveFont} title={t.common.save} aria-label={t.common.save} disabled={isSavingManual}>
                        <Check className="w-3.5 h-3.5" style={{ color: 'var(--color-success, #16a34a)' }} />
                      </button>
                      <button type="button" onClick={() => setEditingFontIdx(null)} title={t.common.cancel} aria-label={t.common.cancel}>
                        <X className="w-3.5 h-3.5" style={{ color: 'var(--color-text-secondary)' }} />
                      </button>
                    </div>
                  ) : (
                    <div
                      key={font.family + idx}
                      className="px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-2"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border)',
                        fontFamily: font.is_google_font ? `${font.family}, sans-serif` : undefined,
                      }}
                    >
                      <span className="font-medium">{font.family}</span>
                      {font.weights.length > 0 && (
                        <span className="text-[10px] opacity-60">{font.weights.join(' · ')}</span>
                      )}
                      {!readOnly && (
                        <span className="inline-flex items-center gap-1 ml-1">
                          <button type="button" onClick={() => startEditFont(idx)} title={t.common.edit} aria-label={t.common.edit} disabled={isSavingManual}>
                            <Edit3 className="w-3 h-3" style={{ color: 'var(--color-text-secondary)' }} />
                          </button>
                          <button type="button" onClick={() => handleRemoveFont(idx)} title={bc.remove ?? 'Supprimer'} aria-label={bc.remove ?? 'Supprimer'} disabled={isSavingManual}>
                            <Trash2 className="w-3 h-3" style={{ color: 'var(--color-danger)' }} />
                          </button>
                        </span>
                      )}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              {bc.images ?? 'Images'}
            </div>
            {!config?.images || config.images.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{bc.imagesEmpty ?? 'No images detected'}</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {config.images.map((image, idx) => (
                  <div
                    key={image.url + idx}
                    className="rounded-lg overflow-hidden relative group"
                    style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
                  >
                    <div
                      className="aspect-video flex items-center justify-center"
                      style={{ backgroundColor: 'var(--color-background)' }}
                    >
                      <img
                        src={image.url}
                        alt={image.alt ?? image.type}
                        className="max-w-full max-h-full object-contain"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                    <div className="px-2 py-1.5 text-[10px] uppercase font-medium flex items-center justify-between" style={{ color: 'var(--color-text-secondary)' }}>
                      <span>{getImageTypeLabel(image.type)}</span>
                      {!readOnly && (
                        <span className="inline-flex items-center gap-1.5">
                          {/* Remplacer l'image via upload MinIO (input file masqué). */}
                          <label title={bc.replace ?? 'Remplacer'} style={{ cursor: isSavingManual ? 'not-allowed' : 'pointer', display: 'inline-flex' }}>
                            <Upload className="w-3 h-3" style={{ color: 'var(--color-text-secondary)' }} />
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={isSavingManual}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleReplaceImage(idx, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            title={bc.remove ?? 'Supprimer'}
                            aria-label={bc.remove ?? 'Supprimer'}
                            disabled={isSavingManual}
                          >
                            <Trash2 className="w-3 h-3" style={{ color: 'var(--color-danger)' }} />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
